<template>
  <div v-if="isVisible" :class="$style.overlay">
    <div :class="$style.dialog">
      <div :class="$style.header">
        <h3>고객 정보 입력</h3>
        <button :class="$style.closeButton" @click="handleClose">✕</button>
      </div>

      <div :class="$style.content">
        <p>상담원 연결을 위해 이름과 전화번호를 입력해주세요.</p>

        <div :class="$style.form">
          <div :class="$style.formGroup">
            <label for="customerName">이름</label>
            <input
              id="customerName"
              v-model="name"
              :class="$style.input"
              :disabled="loading"
              placeholder="이름 입력"
              required
              type="text"
            />
          </div>

          <div :class="$style.formGroup">
            <label for="customerPhone">전화번호</label>
            <input
              id="customerPhone"
              :class="$style.input"
              :disabled="loading"
              :value="phone"
              placeholder="010-1234-5678"
              required
              type="tel"
              @input="handlePhoneInput"
            />
            <!-- 전화번호 형식 안내 메시지 -->
            <p v-if="phone && !isValidPhone(phone)" :class="$style.info">올바른 전화번호 형식을 입력해주세요.</p>
          </div>

          <div v-if="errorMessage" :class="$style.error">
            {{ errorMessage }}
          </div>
        </div>
      </div>

      <div :class="$style.footer">
        <button :class="$style.cancelButton" :disabled="loading" @click="handleClose">취소</button>
        <button :class="$style.submitButton" :disabled="!isValid || loading" @click="handleSubmit">
          {{ loading ? "제출 중..." : "제출" }}
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, nextTick, ref } from "vue"
import { useTicket } from "@/composables/useTicket"
import type { CustomerSnapshot, Ticket } from "@wowpress/types"

// Props
const props = defineProps<{
  isVisible: boolean
  ticket: Ticket
}>()

// Emits
const emit = defineEmits<{
  (e: "close-dialog"): void
  (e: "submit-success", name: string): void
  (e: "submit-failure", error: Error): void
}>()

// State
const name = ref("")
const phone = ref("")
const errorMessage = ref("")
const loading = ref(false)

// Ticket composable
const { updateTicket } = useTicket()

// 전화번호 포맷팅 (xxx-xxxx-xxxx 형식)
const formatPhoneNumber = (phoneNumber: string): string => {
  // 숫자만 추출
  const cleaned = phoneNumber.replace(/\D/g, "")

  // 숫자가 없으면 빈 문자열 반환
  if (!cleaned) return ""

  // 첫 3자리
  let formatted = cleaned.substring(0, 3)

  // 중간 4자리 (있는 경우)
  if (cleaned.length >= 4) {
    formatted += "-" + cleaned.substring(3, 7)
  }

  // 마지막 4자리 (있는 경우)
  if (cleaned.length >= 8) {
    formatted += "-" + cleaned.substring(7, 11)
  }

  return formatted
}

// 전화번호 입력 핸들러
const handlePhoneInput = (e: Event) => {
  const input = e.target as HTMLInputElement
  const cursorPosition = input.selectionStart
  const previousValue = phone.value

  // 포맷팅된 값 계산
  const formattedValue = formatPhoneNumber(input.value)
  phone.value = formattedValue

  // 커서 위치 조정
  nextTick(() => {
    // 이전 값과 새 값의 길이 차이 계산
    const lengthDiff = formattedValue.length - previousValue.length

    // 커서 위치 조정 (하이픈이 추가되었을 때 커서가 뒤로 밀리는 것 방지)
    if (cursorPosition !== null) {
      const newPosition = cursorPosition + lengthDiff
      input.setSelectionRange(newPosition, newPosition)
    }
  })
}

// 전화번호 유효성 검사 (하이픈 허용, 10-11자리 숫자)
const isValidPhone = (phoneNumber: string): boolean => {
  const cleaned = phoneNumber.replace(/-/g, "")
  const phoneRegex = /^01[016789]\d{7,8}$/
  return phoneRegex.test(cleaned)
}

// Computed
const isValid = computed(() => {
  return name.value.trim() !== "" && phone.value.trim() !== "" && isValidPhone(phone.value)
})

// Methods
const handleClose = () => {
  name.value = ""
  phone.value = ""
  errorMessage.value = ""
  emit("close-dialog")
}

const handleSubmit = async () => {
  if (!isValid.value) {
    if (name.value.trim() === "") {
      errorMessage.value = "이름을 입력해주세요."
    } else if (phone.value.trim() === "") {
      errorMessage.value = "전화번호를 입력해주세요."
    } else if (!isValidPhone(phone.value)) {
      errorMessage.value = "올바른 전화번호 형식을 입력해주세요."
    }
    return
  }

  loading.value = true
  errorMessage.value = ""
  let submitError: Error | null = null

  try {
    // 전화번호가 이미 xxx-xxxx-xxxx 형식으로 포맷팅되어 있으므로 그대로 사용
    const formattedPhone = phone.value

    // 기존 고객 정보 가져오기 (id, email 등 유지)
    const existingCustomer: Partial<CustomerSnapshot> = props.ticket.customer || {}

    // 새로운 정보와 병합 (id가 없는 경우를 대비하여 안전하게 처리)
    const updatedCustomerData: CustomerSnapshot = {
      ...existingCustomer, // 기존 정보 복사
      id: existingCustomer.id || props.ticket.customer?.id || "", // ID는 반드시 유지 또는 props에서 가져옴
      name: name.value.trim(), // 이름 업데이트
      phone: formattedPhone, // 포맷팅된 전화번호 업데이트
    }

    // ID가 없는 경우 에러 처리 (이런 경우는 없어야 하지만 방어 코드)
    if (!updatedCustomerData.id) {
      throw new Error("고객 ID를 찾을 수 없습니다. 티켓 데이터 확인이 필요합니다.")
    }

    await updateTicket(props.ticket.id, {
      // ticket.id 사용
      customer: updatedCustomerData, // 병합된 고객 정보로 업데이트
    })

    console.log("[CustomerInfoDialog] Ticket updated successfully with merged customer data")
    emit("submit-success", name.value.trim())
  } catch (error: any) {
    console.error("[CustomerInfoDialog] Ticket update failed:", error)
    submitError = error instanceof Error ? error : new Error("정보 업데이트 중 오류가 발생했습니다.")
    errorMessage.value = submitError.message
    emit("submit-failure", submitError)
  } finally {
    loading.value = false
  }
}
</script>

<style lang="less" module scoped>
/* LoginDialog.vue의 스타일과 거의 동일하게 유지 */
.overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000; /* 다른 요소 위에 표시되도록 z-index 설정 */
}

.dialog {
  width: 100%;
  max-width: 400px;
  background-color: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  animation: fadeIn 0.2s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.header {
  padding: 16px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #eee;

  h3 {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: #333;
  }
}

.closeButton {
  background: transparent;
  border: none;
  font-size: 18px;
  color: #666;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s;

  &:hover {
    color: #333;
  }
}

.content {
  padding: 20px;

  p {
    margin: 0 0 20px 0;
    color: #555;
    font-size: 14px;
  }

  p.info {
    /* 전화번호 형식 안내 메시지 스타일 */
    font-size: 12px;
    color: #888;
    margin: 4px 0 0 0;
  }
}

.form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.formGroup {
  display: flex;
  flex-direction: column;
  gap: 6px;

  label {
    font-size: 14px;
    font-weight: 500;
    color: #333;
  }
}

.input {
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #cf1578; /* Wowpress primary color */
  }

  &:disabled {
    background-color: #f5f5f5;
    cursor: not-allowed;
  }

  &[required] {
    /* 필수 필드 표시 (선택적) */
  }
}

.error {
  color: #e53e3e;
  font-size: 13px;
  margin-top: 4px;
}

.footer {
  padding: 16px 20px;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  border-top: 1px solid #eee;
}

.cancelButton,
.submitButton {
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: opacity 0.2s;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.cancelButton {
  background-color: #f5f5f5;
  color: #333;
  border: 1px solid #ddd;

  &:hover:not(:disabled) {
    background-color: #eee;
  }
}

.submitButton {
  background-color: #cf1578; /* Wowpress primary color */
  color: white;
  border: none;

  &:hover:not(:disabled) {
    opacity: 0.9;
  }
}
</style>
